import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { updateUserPassword } from '@/utils/ERPUtils';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

import { logger } from '@/utils/logger';
interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// 0-4 heuristic for the strength meter: length milestones, mixed case,
// digits, symbols. Display-only - the enforced rule stays "min 8 chars".
const passwordScore = (pw: string): number => {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
};

const SCORE_COLORS = ['', 'bg-red-400', 'bg-amber-400', 'bg-yellow-300', 'bg-emerald-400'];

const fieldLabelClass =
  'text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground';

export const ChangePasswordDialog = ({ open, onOpenChange }: ChangePasswordDialogProps) => {
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    logoutAllSessions: false
  });

  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all password fields.',
        variant: 'destructive'
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: 'Validation Error',
        description: 'Passwords do not match.',
        variant: 'destructive'
      });
      return;
    }

    if (formData.newPassword.length < 8) {
      toast({
        title: 'Validation Error',
        description: 'Password must be at least 8 characters long.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const result = await updateUserPassword(
        user?.email || user?.id || '',
        formData.oldPassword,
        formData.newPassword,
        formData.logoutAllSessions
      );

      if (result.success) {
        toast({
          title: 'Success',
          description: formData.logoutAllSessions
            ? 'Password updated successfully. You will be logged out from all devices.'
            : 'Password updated successfully.',
          variant: 'default'
        });

        // Reset form
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
          logoutAllSessions: false
        });

        // Close dialog
        onOpenChange(false);

        // If logout all sessions is enabled, sign out the user
        if (formData.logoutAllSessions) {
          setTimeout(async () => {
            await signOut();
            navigate('/login');
          }, 1500);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update password.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      logger.error('Update password error:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      logoutAllSessions: false
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Update your account password. Make sure to use a strong password.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="oldPassword" className={fieldLabelClass}>Current Password</Label>
            <div className="relative">
              <Input
                id="oldPassword"
                type={showPasswords.oldPassword ? "text" : "password"}
                placeholder="Enter current password"
                value={formData.oldPassword}
                onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
                disabled={loading}
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, oldPassword: !showPasswords.oldPassword })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={loading}
              >
                {showPasswords.oldPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword" className={fieldLabelClass}>New Password (min 8 characters)</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPasswords.newPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                disabled={loading}
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, newPassword: !showPasswords.newPassword })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={loading}
              >
                {showPasswords.newPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {/* Strength meter - display-only heuristic */}
            <div className="flex gap-1.5 pt-1" aria-hidden="true">
              {[1, 2, 3, 4].map((seg) => {
                const score = passwordScore(formData.newPassword);
                return (
                  <span
                    key={seg}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      seg <= score ? SCORE_COLORS[score] : 'bg-muted'
                    }`}
                  />
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className={fieldLabelClass}>Confirm New Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showPasswords.confirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                disabled={loading}
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirmPassword: !showPasswords.confirmPassword })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={loading}
              >
                {showPasswords.confirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="logoutAllSessions"
              checked={formData.logoutAllSessions}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, logoutAllSessions: checked as boolean })
              }
              disabled={loading}
            />
            <Label
              htmlFor="logoutAllSessions"
              className="text-sm font-normal cursor-pointer"
            >
              Logout from all devices after password change
            </Label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
